<template>
  <v-form id="oleo100-curative-form" ref="form">
    <div v-if="draftLoading" class="d-flex justify-center py-12">
      <v-progress-circular indeterminate color="primary"/>
    </div>

    <template v-else>
      <p class="mt-5">Vérifier et cocher dans le cas échéant les points ci-dessous.</p>

      <PreventivePlan v-model="preventivePlan" :saveEvent="save_event"/>
      <Security v-model="security"/>
      <GlobalView v-model="globalView"/>
      <Manhole v-model="manhole"/>
      <VariousVisualControls v-model="variousVisualControls"/>
      <ControlOfTheDistributor v-model="controlOfTheDistributor"/>
      <NonComplianceReport v-model="nonComplianceReport"/>

      <v-card class="mt-5">
        <v-card-text class="pt-0 fill-height">
          <v-checkbox class="mb-n4 w_70p" v-model="preventivePlan.presenceOfTheCustomer" label="Présence du client"/>
          <v-text-field v-model="preventivePlan.clientName" v-if="preventivePlan.presenceOfTheCustomer"
                        label="Nom du client" :rules="[v => !!v]"></v-text-field>
          <AppSignature v-model="preventivePlan.clientSignature" v-if="preventivePlan.presenceOfTheCustomer" class="mt-4"
                        style="height: auto" label="Signature du client" :saveEvent="save_event" ref="signatureClient"/>
        </v-card-text>
      </v-card>

      <v-card class="mt-5">
        <AppSignature v-model="provider_signature" label="Signature du prestataire *" :saveEvent="save_event" ref="signaturePresta"/>
      </v-card>

      <v-btn class="mt-3 maintenance-detail-actions" height="40px" color="primary" @click="validate">
        {{ $t("maintenance_detail.generate_intervention_report") }}
      </v-btn>
    </template>

    <!-- Floating save button -->
    <v-btn
      fab
      fixed
      bottom
      right
      color="secondary"
      :loading="draftSaving"
      style="bottom: 80px"
      @click="saveFormDraft"
    >
      <v-icon>mdi-content-save</v-icon>
    </v-btn>

    <!-- Draft restore banner -->
    <v-snackbar v-model="draftRestored" top color="info" timeout="5000">
      <v-icon left>mdi-history</v-icon>
      Brouillon restauré — {{ draftSavedAt }}
      <template #action>
        <v-btn text @click="draftRestored = false">OK</v-btn>
      </template>
    </v-snackbar>
  </v-form>
</template>

<script>
import AppSignature from "@/components/MaintenanceViewReport/ImplementedForms/Oleo100Preventive/App/Signature";
import PreventivePlan from "@/components/MaintenanceViewReport/ImplementedForms/Oleo100Preventive/Cards/PreventivePlan";
import Security from "@/components/MaintenanceViewReport/ImplementedForms/Oleo100Preventive/Cards/Security";
import GlobalView from "@/components/MaintenanceViewReport/ImplementedForms/Oleo100Preventive/Cards/GlobalView";
import Manhole from "@/components/MaintenanceViewReport/ImplementedForms/Oleo100Preventive/Cards/Manhole";
import VariousVisualControls
  from "@/components/MaintenanceViewReport/ImplementedForms/Oleo100Preventive/Cards/VariousVisualControls";
import ControlOfTheDistributor
  from "@/components/MaintenanceViewReport/ImplementedForms/Oleo100Preventive/Cards/ControlOfTheDistributor";
import NonComplianceReport
  from "@/components/MaintenanceViewReport/ImplementedForms/Oleo100Preventive/Cards/NonComplianceReport.vue";
import MaintenanceRepository from "@/repositories/MaintenanceRepository";
import {routes} from "@/models/Pages";
import { MANUAL_TRIGGER_VALUES } from './constants/triggers';
import { saveDraft, loadDraft, deleteDraft } from '@/services/draftService';

export default {
  name: "Oleo100Preventive",
  components: {
    NonComplianceReport,
    AppSignature,
    PreventivePlan,
    Security,
    GlobalView,
    Manhole,
    VariousVisualControls,
    ControlOfTheDistributor,
  },
  data() {
    return {
      preventivePlan: {
        hasPreventionPlan: "false",
        viewPreventionPlan: false,
        presenceOfTheCustomer: true,
        clientSignature: "",
        clientName: "",
        preventionPlan: null,
        comment: null,
      },
      security: {
        hasScamLadder: false,
        hasHarnessWithLifeLine: false,
        hasPersonalProtectiveEquipment: false,
        commentScamLadder: "",
        commentHarnessWithLifeLine: "",
      },
      globalView: {
        tank: null,
        hasAnyDefectsFound: false,
        defectsFound: null,
      },
      manhole: {
        lastBattery: 0,
        dtdHistory: [],
        offset: null,
        initialOffset: null,
        changeBattery: false,
        activateSensorAfterChange: false,
        lastMeasureCheck: {
          value: -1,
          expected: 0,
          valid: true,
        },
        offsetCorrection: {
          checked: "true",
          comment: "",
          gauging: null,
        },
        localDisplayState: {
          checked: "true",
          comment: "",
          files: null,
        },
        controlOfTheLeakDetector: false,
        leakDetectorCompliant: "true",
        needsComplementAntifreeze: "false",
        antifreezeVolume: "1",
        controlOfWeldsAndJoints: {
          checked: false,
          compliant: "true",
          comment: "",
          files: null,
        },
        device: {
          nb_releve_par_jour: null,
        },
        changeDevice: false,
      },
      variousVisualControls: {
        floorChecks: {
          checked: false,
          compliant: "true",
          comment: "",
          files: null,
        },
        tankConditions: {
          checked: false,
          compliant: "true",
          comment: "",
          files: null,
        },
        pipingChecking: {
          checked: false,
          compliant: "true",
          comment: "",
          files: null,
        },
        regulatoryPlates: {
          commentFourdataQrCode: '',
          checked: true,
          tippingIdentification: "true",
          fourdataQRCode: "true",
          pastPoster: false,
          pellingPoster: false,
          posterImage: null,
          nameplateImage: null,
          qrCodeImage: null,
          isQrCodeWellAssociated: "true",
          regenerateQrCode: null
        },
      },
      controlOfTheDistributor: {
        filterReplacement: false,
        cartridgeFilterControl: {
          checked: "false",
          cartridgeFilter: {
            comment: "",
            files: null,
          },
          filterStock: {
            checked: false,
            hasFilterStock: "true",
            inStock: 0,
          },
        },
        filterBypassOpeningControl: false,
        presenceSticker: false,
        isValveSealed: "true",
        valvePicture: null,
        switchOffMotor: "false",
        tensionControl: "false",
        filtrationControl: "false",
        resumptionOfTension: "false",
        changingBelt: "false",
        imageBelt: null,
        cleaning: false,
      },
      nonComplianceReport: {
        comment: "",
      },
      save_event: false,
      provider_signature: "",
      draftSaving: false,
      draftRestored: false,
      draftSavedAt: null,
      draftLoading: true,
      validating: false,
    };
  },
  methods: {
    async saveFormDraft() {
      this.draftSaving = true
      try {
        const uid = this.$store.getters["maintenance/get"].uid
        await saveDraft(uid, {
          preventivePlan: this.preventivePlan,
          security: this.security,
          globalView: this.globalView,
          manhole: this.manhole,
          variousVisualControls: this.variousVisualControls,
          controlOfTheDistributor: this.controlOfTheDistributor,
          nonComplianceReport: this.nonComplianceReport,
          provider_signature: this.provider_signature,
        })
        this.$store.dispatch("snackbar/show", {
          snackbar_text: "Brouillon sauvegardé",
          snackbar_type: "success",
        })
      } catch {
        this.$store.dispatch("snackbar/show", {
          snackbar_text: "Erreur lors de la sauvegarde du brouillon",
          snackbar_type: "error",
        })
      } finally {
        this.draftSaving = false
      }
    },
    async validate() {
      const valid = await this.$refs.form.validate()

      if (!valid) {
        this.$nextTick(() => {
          const firstError = this.$el.querySelector('.v-input.error--text');
          if (firstError) {
            const labelEl = firstError.querySelector('.v-label')?.textContent?.trim();
            const placeholder = firstError.querySelector('input[placeholder], textarea[placeholder]')?.getAttribute('placeholder');
            const label = labelEl || placeholder;
            const message = label
              ? `Le champ "${label.replace(/\s*\*$/, '').trim()}" est obligatoire`
              : 'Les champs suivie d\'une "*" sont obligatoires';
            this.$store.dispatch("snackbar/show", {
              snackbar_text: message,
              snackbar_type: "error",
            });
            firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
          } else {
            this.$store.dispatch("snackbar/show", {
              snackbar_text: 'Les champs suivie d\'une "*" sont obligatoires',
              snackbar_type: "error",
            });
          }
        });
        return;
      }

      const presta = this.$refs.signaturePresta;
      if (!presta.checkSignature()) {
        this.$store.dispatch("snackbar/show", {
          snackbar_text: 'La signature du prestataire est obligatoire',
          snackbar_type: "error",
        });
        this.$nextTick(() => {
          const signatureEl = this.$refs.signaturePresta.$el;
          if (signatureEl) signatureEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
        return;
      }

      if (this.validating) return;
      this.validating = true;
      try {
        // Photos are compressed asynchronously (see AppImageInput): the form can already
        // be "valid" (a photo was picked) while compression hasn't finished feeding the
        // report's actual data yet. We don't disable the button, but we wait here for any
        // in-progress compression to finish (the floating loader "Compression de la photo
        // en cours..." stays visible) before sending, so we don't generate a report with
        // a missing photo.
        if (this.$store.getters["photoCompression/isProcessing"]) {
          this.$store.dispatch("snackbar/show", {
            snackbar_text: "Compression des photos en cours, le rapport sera envoyé automatiquement dès que ce sera terminé...",
            snackbar_type: "info",
          });
          await this.waitForPhotoCompression();
        }

        this.sendRepport();
      } finally {
        this.validating = false;
      }
    },
    waitForPhotoCompression() {
      if (!this.$store.getters["photoCompression/isProcessing"]) {
        return Promise.resolve();
      }
      return new Promise((resolve) => {
        const unwatch = this.$store.watch(
          (state, getters) => getters["photoCompression/isProcessing"],
          (isProcessing) => {
            if (!isProcessing) {
              unwatch();
              resolve();
            }
          }
        );
      });
    },
    async sendRepport() {
      this.save_event = true;
      let data = new FormData();

      data.append("json_form", JSON.stringify(this.formatData()));
      data.append("global_data", JSON.stringify(await this.globalData()));
      data.append("preventionPlan", this.preventivePlan.preventionPlan);

      this.$store.dispatch("ajax/in_recovery", {action: "Envoie du rapport"}, {root: true});
      MaintenanceRepository.validate(data).then((response) => {
        this.$store.dispatch("ajax/is_available", {}, {root: true});
        if (response.status === 200) {
          this.report = null;

          this.$store.dispatch("maintenance/load");
          if (this.$store.getters["authentication/access_maintenance_list"]) {
            this.$store.dispatch("maintenance_list/load");
          }

          this.$store.dispatch("snackbar/show", {
            snackbar_text: "Rapport envoyé avec succès",
            snackbar_type: "success",
          });
          this.dialog = false;
          deleteDraft(this.$store.getters["maintenance/get"].uid).catch(() => {});

          const go_back_data = routes[this.$route.name].appbar.go_back;
          return this.$router.push({name: go_back_data.page, params: go_back_data.params()});
        } else {
          this.$store.dispatch("snackbar/show", {
            snackbar_text: response.data.data.error,
            snackbar_type: "error",
          });
        }
      });
    },
    async globalData() {
      await new Promise((resolve) => setTimeout(resolve, 500));
      return {
        preventivePlan: {
          hasPreventionPlan: this.preventivePlan.hasPreventionPlan,
          viewPreventionPlan: this.preventivePlan.viewPreventionPlan,
          presenceOfTheCustomer: this.preventivePlan.presenceOfTheCustomer,
          providerSignature: this.provider_signature,
          clientSignature: this.preventivePlan.clientSignature,
          clientName: this.preventivePlan.clientName
        },
        battery: this.manhole.lastBattery,
        lastEmisionDate: this.manhole.lastEmisionDate,
      };
    },
    formatData() {
      if (this.controlOfTheDistributor.cartridgeFilterControl.filterStock.inStock === undefined || this.controlOfTheDistributor.cartridgeFilterControl.filterStock.inStock == "") this.controlOfTheDistributor.cartridgeFilterControl.filterStock.inStock = 0;
      return [
        {
          title: "Sécurité",
          content: [
            {
              type: "checkbox",
              label: "Echelle harnachée",
              checked: this.security.hasScamLadder,
            },
            {
              type: "paragraph",
              text: "commentaire pas Echelle harnachée: " + this.security.commentScamLadder,
            },
            {
              type: "checkbox",
              label: "Baudrier avec ligne de vie",
              checked: this.security.hasHarnessWithLifeLine,
            },
            {
              type: "paragraph",
              text: "commentaire pas de ligne de vie: " + this.security.commentHarnessWithLifeLine,
            },
            {
              type: "checkbox",
              label: "EPI : Chaussures de sécurité, casque, lunettes, gants et vêtements à bandes réfléchissantes",
              checked: this.security.hasPersonalProtectiveEquipment,
            },
          ],
        },
        {
          title: "Vue générale",
          content: [
            this.globalView.manhole !== null
                ? {
                  type: "image",
                  legend: "Photo du trou d'homme",
                  files: this.globalView.manhole,
                }
                : {
                  type: "paragraph",
                  text: "Photo du trou d'homme : Non renseigné",
                },
            this.globalView.piping !== null
                ? {
                  type: "image",
                  legend: "Photo des éléments de la tuyauterie",
                  files: this.globalView.piping,
                }
                : {
                  type: "paragraph",
                  text: "Photo des éléments de la tuyauterie : Non renseigné",
                },
            this.globalView.tank !== null
                ? {
                  type: "image",
                  legend: "Photo de la cuve",
                  files: this.globalView.tank,
                }
                : {
                  type: "paragraph",
                  text: "Photo de la cuve : Non renseigné",
                },
          ],
        },
        {
          title: "Trou d’homme, détecteur de fuite, jaugeages électronique et manuel",
          content: [
            {
              type: "paragraph",
              text: "Dernier niveau de batterie : " + this.manhole.lastBattery + "% <i>(Donnée provenant de FUEL IT)</i>",
            },
            ...(this.manhole.dtdHistory.length ? [
              { type: "divider" },
              {
                type: "paragraph",
                text: "<b>Derniers relevés (Donnée provenant de FUEL IT)</b>",
              },
              ...this.manhole.dtdHistory.map(entry => ({
                type: "paragraph",
                text: entry.date_releve + " | Niveau : " + entry.liquid_level + " mm | " + (MANUAL_TRIGGER_VALUES.has(entry.declencheur) ? "Manuel" : "Automatique"),
              })),
              { type: "divider" },
            ] : []),
            {
              type: "checkbox",
              label: "Changement de la batterie du capteur",
              checked: this.manhole.changeBattery,
            },
            this.manhole.changeBattery ? {
              type: "checkbox",
              label: "Activer le capteur après changement de piles",
              checked: this.manhole.activateSensorAfterChange,
            } : {},
            {
              type: "paragraph",
              text: "Nombre de relevés par jour: " + this.manhole.device.nb_releve_par_jour,
            },
            this.manhole.changeDevice === true ?
                {
                  type: "checkbox",
                  label: "Remplacer le capteur par un capteur 6 RJ",
                  checked: this.manhole.changeDevice,
                } : {},
            {
              type: "divider",
            },
            {
              type: "paragraph",
              text: "Jaugeage manuel de la cuve avec mètre ruban : " + (this.manhole.lastMeasureCheck.value != -1 ? (this.manhole.lastMeasureCheck.value + "cm") : 'Non renseigné'),
            },
            {
              type: "paragraph",
              text: "Jaugeage FUEL IT : Hauteur de liquide : " + (this.manhole.lastMeasureCheck.expected + "cm <i>(Donnée provenant de FUEL IT)</i>"),
            },
            {
              type: "paragraph",
              text: this.manhole.lastMeasureCheck.valid ? "Niveau cohérent" : "Niveau incohérent",
            },
            this.manhole.offset !== this.manhole.initialOffset
              ? {
                  type: "paragraph",
                  text: "Offset mis à jour: de " + this.manhole.initialOffset + " mm à " + this.manhole.offset + " mm",
                }
              : {
                  type: "paragraph",
                  text: "Offset : " + this.manhole.offset + " mm (pas de changement)",
                },
            {
              type: "divider",
            },
            {
              type: "comment",
              text: this.manhole.localDisplayState.comment ?? "-",
            },
            this.manhole.localDisplayState.files !== null
                ? {
                  type: "image",
                  files: this.manhole.localDisplayState.files,
                }
                : {
                  type: "paragraph",
                  text: "Photo du capteur : Non renseigné",
                },
            {
              type: "divider",
            },
            this.manhole.controlOfTheLeakDetector && this.manhole.leakDetectorCompliant == "false"
                ? {
                  type: "radiogroup",
                  label: "Besoin complément du produit Antigel",
                  choices: [
                    {
                      type: "radio",
                      label: "Oui",
                      checked: this.manhole.needsComplementAntifreeze === "true",
                    },
                    {
                      type: "radio",
                      label: "Non",
                      checked: this.manhole.needsComplementAntifreeze === "false",
                    },
                  ],
                }
                : {},
            this.manhole.controlOfTheLeakDetector && this.manhole.leakDetectorCompliant == "false" && this.manhole.needsComplementAntifreeze == "true"
                ? {
                  type: "radiogroup",
                  label: "Quantité réassort estimé",
                  choices: [
                    {
                      type: "radio",
                      label: "1L",
                      checked: this.manhole.antifreezeVolume == "1",
                    },
                    {
                      type: "radio",
                      label: "2L",
                      checked: this.manhole.antifreezeVolume == "2",
                    },
                    {
                      type: "radio",
                      label: "5L",
                      checked: this.manhole.antifreezeVolume == "5",
                    },
                  ],
                }
                : {},
            {
              type: "checkbox",
              label: "Trou d’homme, contrôle des soudures et joints",
              checked: this.manhole.controlOfWeldsAndJoints.checked,
            },
            this.manhole.controlOfWeldsAndJoints.checked === true
                ? {
                  type: "radiogroup",
                  label: "Conforme",
                  choices: [
                    {
                      type: "radio",
                      label: "Oui",
                      checked: this.manhole.controlOfWeldsAndJoints.compliant === "true",
                    },
                    {
                      type: "radio",
                      label: "Non",
                      checked: this.manhole.controlOfWeldsAndJoints.compliant === "false",
                    },
                  ],
                }
                : {},
            this.manhole.controlOfWeldsAndJoints.checked === true && this.manhole.controlOfWeldsAndJoints.compliant === "false"
                ? {
                  type: "comment",
                  text: this.manhole.controlOfWeldsAndJoints.comment ?? "-",
                }
                : {},
            this.manhole.controlOfWeldsAndJoints.checked === true && this.manhole.controlOfWeldsAndJoints.compliant === "false" && this.manhole.controlOfWeldsAndJoints.files !== null
                ? {
                  type: "image",
                  files: this.manhole.controlOfWeldsAndJoints.files,
                }
                : {},
          ],
        },
        {
          title: "Contrôles visuels divers - Hors trou d’homme",
          content: [
            this.variousVisualControls.floorChecks.checked === true &&
            this.variousVisualControls.floorChecks.compliant === "false" &&
            this.variousVisualControls.floorChecks.comment !== null &&
            this.variousVisualControls.floorChecks.comment != ""
                ? {
                  type: "comment",
                  text: this.variousVisualControls.floorChecks.comment,
                }
                : {},
            this.variousVisualControls.floorChecks.checked === true && this.variousVisualControls.floorChecks.compliant === "false" && this.variousVisualControls.floorChecks.files !== null
                ? {
                  type: "image",
                  files: this.variousVisualControls.floorChecks.files,
                }
                : {},
            {
              type: "checkbox",
              label: "Contrôle de l’état général de la cuve (défauts peinture, coulures, coups, rouille, usure)",
              checked: this.variousVisualControls.tankConditions.checked,
            },
            this.variousVisualControls.tankConditions.checked === true
                ? {
                  type: "radiogroup",
                  label: "Conforme",
                  choices: [
                    {
                      type: "radio",
                      label: "Oui",
                      checked: this.variousVisualControls.tankConditions.compliant === "true",
                    },
                    {
                      type: "radio",
                      label: "Non",
                      checked: this.variousVisualControls.tankConditions.compliant === "false",
                    },
                  ],
                }
                : {},
            this.variousVisualControls.tankConditions.checked === true &&
            this.variousVisualControls.tankConditions.compliant === "false" &&
            this.variousVisualControls.tankConditions.comment !== null &&
            this.variousVisualControls.tankConditions.comment != ""
                ? {
                  type: "comment",
                  text: this.variousVisualControls.tankConditions.comment,
                }
                : {},
            this.variousVisualControls.tankConditions.checked === true && this.variousVisualControls.tankConditions.compliant === "false" && this.variousVisualControls.tankConditions.files !== null
                ? {
                  type: "image",
                  files: this.variousVisualControls.tankConditions.files,
                }
                : {},
            this.variousVisualControls.pipingChecking.checked === true &&
            this.variousVisualControls.pipingChecking.compliant === "false" &&
            this.variousVisualControls.pipingChecking.comment !== null &&
            this.variousVisualControls.pipingChecking.comment != ""
                ? {
                  type: "comment",
                  text: this.variousVisualControls.pipingChecking.comment,
                }
                : {},
            this.variousVisualControls.pipingChecking.checked === true && this.variousVisualControls.pipingChecking.compliant === "false" && this.variousVisualControls.pipingChecking.files !== null
                ? {
                  type: "image",
                  files: this.variousVisualControls.pipingChecking.files,
                }
                : {},
            {
              type: "checkbox",
              label: "Vérification de la présence des plaques réglementaires",
              checked: this.variousVisualControls.regulatoryPlates.checked,
            },
            this.variousVisualControls.regulatoryPlates.checked === true
                ? {
                  type: "radiogroup",
                  label: "QR Code télémétrie Four Data :",
                  choices: [
                    {
                      type: "radio",
                      label: "Oui",
                      checked: this.variousVisualControls.regulatoryPlates.fourdataQRCode === "true",
                    },
                    {
                      type: "radio",
                      label: "Non",
                      checked: this.variousVisualControls.regulatoryPlates.fourdataQRCode === "false",
                    },
                  ],
                }
                : {},
            this.variousVisualControls.regulatoryPlates.checked === true
                ? {
                  type: "image",
                  legend: "plaque d'identification Oleo100",
                  files: this.variousVisualControls.regulatoryPlates.nameplateImage,
                }
                : {},
            this.variousVisualControls.regulatoryPlates.checked === true && this.variousVisualControls.regulatoryPlates.fourdataQRCode === "true"
                ? {
                  type: "comment",
                  text: this.variousVisualControls.regulatoryPlates.commentFourdataQrCode ?? "",
                }
                : {},
            this.variousVisualControls.regulatoryPlates.pastPoster === true
                ? {
                  type: "checkbox",
                  label: "Collage de l'affiche ainsi que le QR code dans le bon emplacement",
                  checked: this.variousVisualControls.regulatoryPlates.pastPoster,
                } : {},
            this.variousVisualControls.regulatoryPlates.pellingPoster === true
                ? {
                  type: "checkbox",
                  label: "Changer l'affiche si elle est partiellement décollé",
                  checked: this.variousVisualControls.regulatoryPlates.pellingPoster,
                } : {},
            this.variousVisualControls.regulatoryPlates.checked === true && this.variousVisualControls.regulatoryPlates.fourdataQRCode === "false"
                ? {
                  type: "paragraph",
                  text: "<b>(Merci de coller un QR code sur l'affiche et de l'associer au capteur)</b>",
                }
                : {},
            this.variousVisualControls.regulatoryPlates.checked === true
                ? {
                  type: "image",
                  legend: "Photo de l'affiche",
                  files: this.variousVisualControls.regulatoryPlates.posterImage,
                } : {},
            this.variousVisualControls.regulatoryPlates.checked === true
                ? {
                  type: "image",
                  legend: "Photo zoomée du QR code",
                  files: this.variousVisualControls.regulatoryPlates.qrCodeImage,
                }
                : {},
            this.variousVisualControls.regulatoryPlates.checked === true
                ? {
                  type: "radiogroup",
                  label: "QR Code associé au bon numéro du capteur",
                  choices: [
                    {
                      type: "radio",
                      label: "Oui",
                      checked: this.variousVisualControls.regulatoryPlates.isQrCodeWellAssociated === "true",
                    },
                    {
                      type: "radio",
                      label: "Non",
                      checked: this.variousVisualControls.regulatoryPlates.isQrCodeWellAssociated !== "true",
                    },
                  ],
                } : {},
            this.variousVisualControls.regulatoryPlates.checked === true
                ? {
                  type: "image",
                  legend: "Qr code associé au capteur",
                  files: [this.variousVisualControls.regulatoryPlates.regenerateQrCode]
                } : {},
          ],
        },
        {
          title: "Contrôle distributeur",
          content: [
            this.controlOfTheDistributor.cartridgeFilterControl.filterStock.checked === true
                ? {
                  type: "radiogroup",
                  label: " ",
                  choices: [
                    {
                      type: "radio",
                      label: "Existants",
                      checked: this.controlOfTheDistributor.cartridgeFilterControl.filterStock.hasFilterStock === "true",
                    },
                    {
                      type: "radio",
                      label: "Absents",
                      checked: this.controlOfTheDistributor.cartridgeFilterControl.filterStock.hasFilterStock === "false",
                    },
                  ],
                }
                : {},
            this.controlOfTheDistributor.cartridgeFilterControl.filterStock.checked === true && this.controlOfTheDistributor.cartridgeFilterControl.filterStock.hasFilterStock === "true"
                ? {
                  type: "paragraph",
                  text: "Le client a " + this.controlOfTheDistributor.cartridgeFilterControl.filterStock.inStock + " filtre(s).",
                }
                : {},
            {
              type: "checkbox",
              label: 'Présence du Sticker "Numéro vert Oleo100"',
              checked: this.controlOfTheDistributor.presenceSticker,
            },
            {
              type: "paragraph",
              text: "La vanne de bypass est-elle bien plombée: " + (this.controlOfTheDistributor.isValveSealed === "true" ? "Oui" : "Non"),
            },
            this.controlOfTheDistributor.valvePicture !== null
                ? {
                  type: "image",
                  legend: "Photo de la vanne de bypass",
                  files: this.controlOfTheDistributor.valvePicture,
                }
                : {
                  type: "paragraph",
                  text: "Photo de la vanne de bypass : Non renseigné",
                },
            this.controlOfTheDistributor.filtrationPicture !== null
                ? {
                  type: "image",
                  legend: "Photo de la filtration",
                  files: this.controlOfTheDistributor.filtrationPicture,
                }
                : {
                  type: "paragraph",
                  text: "Photo de la filtration : Non renseigné",
                },
            this.controlOfTheDistributor.stickerPicture !== null
                ? {
                  type: "image",
                  legend: "Photo du Sticker vert Oleo100",
                  files: this.controlOfTheDistributor.stickerPicture,
                }
                : {
                  type: "paragraph",
                  text: "Photo du Sticker vert Oleo100 : Non renseigné",
                },
            this.controlOfTheDistributor.distributeurPicture !== null
                ? {
                  type: "image",
                  legend: "Photos des 4 côtés du distributeur",
                  files: this.controlOfTheDistributor.distributeurPicture,
                }
                : {
                  type: "paragraph",
                  text: "Photos des 4 côtés du distributeur : Non renseigné",
                },
            this.controlOfTheDistributor.tensionControl === 'true'
                ? {
                  type: "radiogroup",
                  label: "Reprise de la tension :",
                  choices: [
                    {
                      type: "radio",
                      label: "Oui",
                      checked: this.controlOfTheDistributor.resumptionOfTension === "true",
                    },
                    {
                      type: "radio",
                      label: "Non",
                      checked: this.controlOfTheDistributor.resumptionOfTension === "false",
                    },
                    {
                      type: "radio",
                      label: "Non applicable",
                      checked: this.controlOfTheDistributor.resumptionOfTension === "NonApplicable",
                    },
                  ],
                }
                : {},
            this.controlOfTheDistributor.tensionControl === 'true'
                ? {
                  type: "radiogroup",
                  label: "Changement de la courroie :",
                  choices: [
                    {
                      type: "radio",
                      label: "Oui",
                      checked: this.controlOfTheDistributor.changingBelt === "true",
                    },
                    {
                      type: "radio",
                      label: "Non",
                      checked: this.controlOfTheDistributor.changingBelt === "false",
                    },
                  ],
                }
                : {},
            this.controlOfTheDistributor.tensionControl === 'true' && this.controlOfTheDistributor.changingBelt === "true"
                ? {
                  type: "image",
                  files: this.controlOfTheDistributor.imageBelt,
                }
                : {},
          ],
        },
        {
          title: "Rapport de non-conformité",
          content: [
            {
              type: "comment",
              text: this.nonComplianceReport.comment ?? "-",
            },
          ]
        }
      ];
    },
  },
  async mounted() {
    const maintenance = this.$store.getters["maintenance/get"]
    this.preventivePlan.comment = maintenance.comment
    this.manhole.device = maintenance.device

    const draft = await loadDraft(maintenance.uid).catch(() => null)
    let restoredSignatures = null

    if (draft) {
      const d = draft.data
      if (d.preventivePlan) {
        this.preventivePlan = d.preventivePlan
      }
      if (d.security) {
        this.security = d.security
      }
      if (d.globalView) {
        this.globalView = d.globalView
      }
      if (d.manhole) {
        this.manhole = {...d.manhole, device: maintenance.device}
      }
      if (d.variousVisualControls) {
        this.variousVisualControls = d.variousVisualControls
      }
      if (d.controlOfTheDistributor) {
        this.controlOfTheDistributor = d.controlOfTheDistributor
      }
      if (d.nonComplianceReport) {
        this.nonComplianceReport = d.nonComplianceReport
      }
      if (d.provider_signature) {
        restoredSignatures = {presta: d.provider_signature, client: d.preventivePlan?.clientSignature}
      }

      this.draftSavedAt = new Date(draft.savedAt).toLocaleString('fr-FR')
      this.draftRestored = true
    }

    // Reveal the form — child components will mount now with restored data
    this.draftLoading = false

    // Restore signature canvases after children have mounted
    if (restoredSignatures) {
      this.$nextTick(() => {
        if (restoredSignatures.presta && this.$refs.signaturePresta?.$refs.signature) {
          this.$refs.signaturePresta.$refs.signature.fromDataURL(restoredSignatures.presta)
        }
        if (restoredSignatures.client && this.$refs.signatureClient?.$refs.signature) {
          this.$refs.signatureClient.$refs.signature.fromDataURL(restoredSignatures.client)
        }
      })
    }
  },
};
</script>

<style scoped>
#oleo100-curative-form {
  width: 100%;
  max-width: 500px;
}
</style>